import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { cohortQueryKeys, fetchCohortVersion } from '@verantos-dev/data-access-cohorts';

import { useAutosaveStagedDefinition } from './useAutosaveStagedDefinition';
import type { UseConfirmCohortPickArgs, UseConfirmCohortPickResult } from '../types';

// Commits the highlighted pick: autosaves the staged definition with the new
// cohort, warms the new cohort's version query, and closes the modal. Isolated
// from list/search and selection state so the submit side effect can be tested
// independently.
export const useConfirmCohortPick = ({
  projectId,
  cohortList,
  selectedId,
  currentCohortId,
  isLoading,
  onClose,
}: UseConfirmCohortPickArgs): UseConfirmCohortPickResult => {
  const queryClient = useQueryClient();
  const { autosave, isSaving, saveError } = useAutosaveStagedDefinition(projectId);

  const onConfirm = useCallback(() => {
    if (!selectedId) return;
    if (selectedId === currentCohortId) {
      onClose();
      return;
    }

    autosave({ kind: 'setCohort', cohortProfileId: selectedId }, () => {
      // Warm the cohort version the Study period section reads for the new pick.
      const cohortVersionId = cohortList.find(
        (c) => c.cohortProfileId === selectedId
      )?.latestCohortVersionId;
      if (cohortVersionId) {
        queryClient.prefetchQuery({
          queryKey: cohortQueryKeys.version(cohortVersionId),
          queryFn: () => fetchCohortVersion(projectId, selectedId, cohortVersionId),
        });
      }
      onClose();
    });
  }, [selectedId, currentCohortId, autosave, cohortList, queryClient, projectId, onClose]);

  const canConfirm = selectedId !== null && selectedId !== currentCohortId && !isLoading;

  return { onConfirm, canConfirm, isSubmitting: isSaving, submitError: saveError };
};
